import { useNavigate } from '@tanstack/react-router';
import { type MouseEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ChatHistorySidebar, DeleteChatDialog } from '@/components/case-partner/components';
import { buildLoadedChatSelections } from '@/components/case-partner/hooks';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useCasePartner } from '@/components/contexts/CasePartnerContext';
import { type ChatSession } from '@/hooks/useChat';
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { apiErrorMessage } from '@/lib/api-client';

interface CasePartnerHistoryColumnProps {
  slug: string;
  startClosed?: boolean;
  onChatLoad: (chatGuid: string) => void;
  onNewChat: () => void;
}

export const CasePartnerHistoryColumn = ({
  slug,
  startClosed = false,
  onChatLoad,
  onNewChat,
}: CasePartnerHistoryColumnProps) => {
  const navigate = useNavigate();
  const { selectedCase } = useCaseContext();
  const casePartner = useCasePartner();
  const { tokenLimitInfo } = useTokenLimit();
  const isComposingNewChatRef = useRef(casePartner.isComposingNewChat);
  isComposingNewChatRef.current = casePartner.isComposingNewChat;
  const currentChatGuidRef = useRef(casePartner.currentChatGuid);
  currentChatGuidRef.current = casePartner.currentChatGuid;

  const {
    isSidebarVisible,
    setIsSidebarVisible,
    isComposingNewChat,
    currentChatGuid,
    chatHistory,
    streamingChatGuid,
    isLoading,
    chatHistoryHasMore,
    isLoadingMoreChatHistory,
    loadMoreChatHistory,
    searchChatHistory,
    chatHistorySearchQuery,
    chatHistorySearchResults,
    chatHistorySearchHasMore,
    renameChat,
    startNewChat,
    deleteChat,
    isDeleteDialogOpen,
    closeDeleteDialog,
    openDeleteDialog,
    chatToDelete,
    setRouterQueryDocuments,
    setUserInput,
    setComposerQuote,
    clearSelections,
    setShowAllDocuments,
    setShowPromptSuggestions,
    setHasShownInitialSuggestions,
    setIsComposingNewChat,
  } = casePartner;

  const displayedCurrentChatGuid = isComposingNewChat ? null : currentChatGuid;
  const startClosedRef = useRef(startClosed);
  const [hasAppliedDefault, setHasAppliedDefault] = useState(false);

  useLayoutEffect(() => {
    setIsSidebarVisible(!startClosedRef.current);
    setHasAppliedDefault(true);
  }, [setIsSidebarVisible]);

  const historyVisible = hasAppliedDefault ? isSidebarVisible : !startClosed;

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarVisible(false);
  }, [setIsSidebarVisible]);

  const handleStartNewChat = useCallback(() => {
    if (isComposingNewChatRef.current) {
      History.prototype.replaceState.call(
        window.history,
        window.history.state,
        '',
        `/matter/${slug}/case-partner`,
      );
      return;
    }

    isComposingNewChatRef.current = true;
    setRouterQueryDocuments(undefined);
    setUserInput('');
    setComposerQuote(null);
    clearSelections();
    setShowAllDocuments(false);
    setShowPromptSuggestions(false);
    setHasShownInitialSuggestions(false);
    startNewChat();
    setIsComposingNewChat(true);
    History.prototype.replaceState.call(
      window.history,
      window.history.state,
      '',
      `/matter/${slug}/case-partner`,
    );
  }, [
    clearSelections,
    setHasShownInitialSuggestions,
    setIsComposingNewChat,
    setRouterQueryDocuments,
    setShowAllDocuments,
    setShowPromptSuggestions,
    setUserInput,
    setComposerQuote,
    slug,
    startNewChat,
  ]);

  const handleChatSelection = useCallback((chat: ChatSession) => {
    isComposingNewChatRef.current = false;
    setIsComposingNewChat(false);
    if (currentChatGuid === chat.guid) {
      casePartner.requestFocusMessage(chat.searchMessageId, chatHistorySearchQuery);
      return;
    }
    setComposerQuote(null);
    setRouterQueryDocuments(undefined);
    clearSelections();
    setShowAllDocuments(false);
    setShowPromptSuggestions(false);
    setHasShownInitialSuggestions(false);
    const targetGuid = chat.guid;
    void casePartner.loadChat(targetGuid, { force: true }).then((result) => {
      if (!result || currentChatGuidRef.current !== targetGuid) return;
      casePartner.setDocumentSelections(
        buildLoadedChatSelections(result.documentSelections, casePartner.documents),
      );
      casePartner.setHasShownInitialSuggestions(true);
      casePartner.requestFocusMessage(chat.searchMessageId, chatHistorySearchQuery);
    }).catch((error) => {
      if (currentChatGuidRef.current !== targetGuid) return;
      toast.error(apiErrorMessage(error) || 'Failed to load chat');
    });
    History.prototype.replaceState.call(
      window.history,
      window.history.state,
      '',
      `/matter/${slug}/case-partner/${chat.guid}`,
    );
  }, [
    casePartner,
    chatHistorySearchQuery,
    clearSelections,
    currentChatGuid,
    setComposerQuote,
    setHasShownInitialSuggestions,
    setIsComposingNewChat,
    setRouterQueryDocuments,
    setShowAllDocuments,
    setShowPromptSuggestions,
    slug,
  ]);

  const handleOpenDeleteDialog = useCallback((guid: string, event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    openDeleteDialog(guid);
  }, [openDeleteDialog]);

  const handleDeleteChat = useCallback(async (guid: string) => {
    closeDeleteDialog();
    const isDeletingCurrentChat = guid === currentChatGuid;

    if (isDeletingCurrentChat) {
      setComposerQuote(null);
    }

    if (isDeletingCurrentChat && slug) {
      await navigate({
        to: '/matter/$slug/case-partner',
        params: { slug },
        resetScroll: false,
      });
      setTimeout(() => deleteChat(guid), 100);
    } else {
      setTimeout(() => deleteChat(guid), 0);
    }
  }, [closeDeleteDialog, currentChatGuid, deleteChat, navigate, setComposerQuote, slug]);

  if (typeof selectedCase === 'undefined') {
    return null;
  }

  return (
    <>
      <DeleteChatDialog
        isOpen={isDeleteDialogOpen}
        chatTitle={chatHistory.find((chat) => chat.guid === chatToDelete)?.title}
        onClose={closeDeleteDialog}
        onConfirm={() => {
          if (chatToDelete) {
            void handleDeleteChat(chatToDelete);
          }
        }}
      />
      <ChatHistorySidebar
        isVisible={historyVisible}
        onClose={handleCloseSidebar}
        chatHistory={chatHistory}
        currentChatGuid={displayedCurrentChatGuid}
        streamingChatGuid={streamingChatGuid}
        isLoading={isLoading && chatHistory.length === 0}
        isNewChatDisabled={isComposingNewChat}
        tokenLimitInfo={tokenLimitInfo}
        onNewChat={handleStartNewChat}
        onChatSelect={handleChatSelection}
        onDeleteClick={handleOpenDeleteDialog}
        onRename={renameChat}
        hasMore={chatHistorySearchQuery ? chatHistorySearchHasMore : chatHistoryHasMore}
        isLoadingMore={isLoadingMoreChatHistory}
        onLoadMore={loadMoreChatHistory}
        onSearchQueryChange={searchChatHistory}
        remoteSearchQuery={chatHistorySearchQuery}
        remoteSearchResults={chatHistorySearchResults}
        closeOnOutsideClick={false}
        layout="docked"
      />
    </>
  );
};
